import React, { useState } from "react";
import {
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Form,
    FormGroup,
    Label,
    Input,
    Button,
    Spinner,
    Row,
    Col
} from "reactstrap";
import { toast } from "react-toastify";
import { fetchPayTRRates, saveGroup } from "../../api/requests/company-requests";
import {
    CARD_TYPES,
    MAX_INSTALLMENTS
} from "../../constants/paymentConstants";
import {
    convertBackendToFrontend,
    convertFrontendToBackend
} from "../../utils/installmentHelpers";

const showToast = (type, msg) => {
    const map = { danger: "error", error: "error" };
    const fn = toast[map[type] || type] || toast.info;
    fn(msg, { autoClose: 3000 });
};

export default function AddGroupModal({
                                          isOpen,
                                          toggle,
                                          companyId,
                                          onSuccess,
                                          isMobile
                                      }) {
    const [loadingGroupSave, setLoadingGroupSave] = useState(false);
    const [groupData, setGroupData] = useState({
        groupName: "",
        description: "",
        rates: convertBackendToFrontend([])
    });

    const handleGroupChange = (e) => {
        const { name, value } = e.target;
        setGroupData((prev) => ({ ...prev, [name]: value }));
    };

    const handleRateChange = (card, inst, newVal) => {
        setGroupData((prev) => ({
            ...prev,
            rates: {
                ...prev.rates,
                [card]: {
                    ...(prev.rates?.[card] || {}),
                    [inst]: {
                        id: prev.rates?.[card]?.[inst]?.id || null,
                        value: newVal,
                        enabled: prev.rates?.[card]?.[inst]?.enabled !== false
                    }
                }
            }
        }));
    };

    const handleEnabledChange = (card, inst, checked) => {
        setGroupData((prev) => ({
            ...prev,
            rates: {
                ...prev.rates,
                [card]: {
                    ...prev.rates[card],
                    [inst]: {
                        id: prev.rates[card]?.[inst]?.id || null,
                        value: prev.rates[card]?.[inst]?.value || "0.00",
                        enabled: checked
                    }
                }
            }
        }));
    };

    const handleFetchPayTRRates = async () => {
        if (!companyId) return;
        try {
            setLoadingGroupSave(true);
            const json = await fetchPayTRRates(companyId);
            if (Array.isArray(json.data)) {
                const payTRRates = convertBackendToFrontend(json.data);
                setGroupData((prev) => ({ ...prev, rates: payTRRates }));
                showToast("success", "PayTR taksit oranları yüklendi");
            } else {
                showToast("danger", json.message || "Oranlar yüklenemedi");
            }
        } catch (err) {
            console.error("PayTR rates error:", err);
            showToast("danger", "PayTR oranları alınamadı");
        } finally {
            setLoadingGroupSave(false);
        }
    };

    const handleSaveGroup = async () => {
        const groupName = groupData?.groupName?.trim();
        if (!groupName) {
            showToast("warning", "Grup adı gereklidir");
            return;
        }

        const payload = {
            groupName: groupName,
            description: groupData?.description?.trim() || "",
            companyId,
            installmentRates: convertFrontendToBackend(groupData?.rates || {})
        };

        try {
            setLoadingGroupSave(true);
            await saveGroup(payload, false);
            showToast("success", "Taksit grubu oluşturuldu");
            onSuccess(); // Ana component'e haber ver
        } catch (error) {
            console.error("Group save error:", error);
            showToast("danger", error?.message || "Kaydetme hatası");
        } finally {
            setLoadingGroupSave(false);
        }
    };

    // Modal açıldığında state'i sıfırla
    React.useEffect(() => {
        if (isOpen) {
            setGroupData({
                groupName: "",
                description: "",
                rates: convertBackendToFrontend([])
            });
            setLoadingGroupSave(false);
        }
    }, [isOpen]);

    return (
        <Modal
            isOpen={isOpen}
            toggle={toggle}
            size={isMobile ? "" : "lg"}
            style={{
                maxWidth: isMobile ? "95vw" : "90vw",
                width: isMobile ? "95vw" : "50vw",
                height: "90vh",
                margin: "5vh auto"
            }}
        >
            <ModalHeader toggle={toggle}>Yeni Taksit Grubu Oluştur</ModalHeader>
            <ModalBody
                style={{
                    height: "calc(90vh - 120px)",
                    overflowY: "auto",
                    overflowX: "hidden",
                    paddingRight: isMobile ? "8px" : "15px"
                }}
            >
                <Form>
                    <FormGroup>
                        <Label>Grup Adı *</Label>
                        <Input
                            name="groupName"
                            value={groupData?.groupName || ""}
                            onChange={handleGroupChange}
                            style={{ borderRadius: "0" }}
                            required
                            disabled={loadingGroupSave}
                            placeholder="Grup adını giriniz"
                        />
                    </FormGroup>
                    <FormGroup>
                        <Label>Açıklama</Label>
                        <Input
                            type="textarea"
                            name="description"
                            value={groupData?.description || ""}
                            onChange={handleGroupChange}
                            style={{ borderRadius: "0" }}
                            rows={3}
                            disabled={loadingGroupSave}
                            placeholder="Grup açıklaması (opsiyonel)"
                        />
                    </FormGroup>
                    <hr />
                    <h5
                        className="mb-2"
                        style={{ fontSize: isMobile ? "1rem" : "1.25rem" }}
                    >
                        Taksit Oranları
                    </h5>
                    <div className="mb-3">
                        <Button
                            color="info"
                            onClick={handleFetchPayTRRates}
                            disabled={loadingGroupSave || !companyId}
                            style={{ marginRight: "1rem", borderRadius: "0" }}
                        >
                            {loadingGroupSave ? (
                                <>
                                    <Spinner size="sm" className="me-2" />
                                    Yükleniyor...
                                </>
                            ) : (
                                "PayTR Taksit Oranlarını Çek"
                            )}
                        </Button>
                    </div>
                    {CARD_TYPES.map((card) => (
                        <div key={card} className="mb-4">
                            <strong
                                className="text-primary"
                                style={{ fontSize: isMobile ? "0.9rem" : "1rem" }}
                            >
                                {card}
                            </strong>
                            <Row className="g-2 mt-1">
                                {[...Array(MAX_INSTALLMENTS)].map((_, i) => {
                                    const inst = i + 1;
                                    const cellData = groupData.rates?.[card]?.[inst];
                                    const isEnabled =
                                        cellData ? cellData.enabled !== false : true;
                                    const cellValue = cellData ? cellData.value || "" : "";

                                    return (
                                        <Col key={inst} xs={12} sm={6} md={4} lg={3} xl={2}>
                                            <div
                                                className="border p-2 p-sm-3"
                                                style={{
                                                    borderRadius: isMobile ? "6px" : "8px",
                                                    backgroundColor: "#f8f9fa"
                                                }}
                                            >
                                                <div
                                                    className="d-flex align-items-center mb-2 mb-sm-3 cursor-pointer"
                                                    onClick={() =>
                                                        handleEnabledChange(card, inst, !cellData?.enabled)
                                                    }
                                                    style={{ cursor: "pointer", userSelect: "none" }}
                                                >
                                                    <input
                                                        className="form-check-input me-2"
                                                        type="checkbox"
                                                        checked={isEnabled}
                                                        readOnly
                                                        style={{
                                                            transform: isMobile ? "scale(0.9)" : "none"
                                                        }}
                                                    />
                                                    <label
                                                        className="form-check-label mb-0 fw-medium"
                                                        style={{
                                                            cursor: "pointer",
                                                            fontSize: isMobile ? "0.8rem" : "0.875rem",
                                                            color: isEnabled ? "#495057" : "#6c757d"
                                                        }}
                                                    >
                                                        {inst} Taksit
                                                    </label>
                                                </div>
                                                <div>
                                                    <Label
                                                        className="mb-2 text-muted fw-medium"
                                                        style={{
                                                            fontSize: isMobile ? "0.75rem" : "0.8rem"
                                                        }}
                                                    >
                                                        Komisyon (%)
                                                    </Label>
                                                    <Input
                                                        type="number"
                                                        step="0.01"
                                                        min="0"
                                                        max="100"
                                                        value={cellValue}
                                                        onChange={(e) =>
                                                            handleRateChange(card, inst, e.target.value)
                                                        }
                                                        disabled={loadingGroupSave || !isEnabled}
                                                        style={{
                                                            borderRadius: "6px",
                                                            border: isEnabled
                                                                ? "1px solid #ced4da"
                                                                : "1px solid #e9ecef",
                                                            backgroundColor: isEnabled ? "#fff" : "#f8f9fa",
                                                            padding: isMobile
                                                                ? "0.25rem 0.5rem"
                                                                : undefined,
                                                            fontSize: isMobile ? "0.875rem" : undefined,
                                                            height: isMobile ? "2rem" : undefined
                                                        }}
                                                        size="sm"
                                                        placeholder="0.00"
                                                    />
                                                </div>
                                            </div>
                                        </Col>
                                    );
                                })}
                            </Row>
                        </div>
                    ))}
                </Form>
            </ModalBody>
            <ModalFooter>
                <Button
                    color="secondary"
                    onClick={toggle}
                    style={{ borderRadius: "0" }}
                    disabled={loadingGroupSave}
                >
                    İptal
                </Button>
                <Button
                    color="success"
                    onClick={handleSaveGroup}
                    style={{ borderRadius: "0" }}
                    disabled={!groupData.groupName?.trim() || loadingGroupSave}
                >
                    {loadingGroupSave ? (
                        <>
                            <Spinner size="sm" className="me-2" />
                            Kaydediliyor...
                        </>
                    ) : (
                        "Kaydet"
                    )}
                </Button>
            </ModalFooter>
        </Modal>
    );
}